// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Flex, type FlexProps } from '@mantine/core';
import type { ICellRendererParams } from 'ag-grid-enterprise';

import { isNotNil } from 'helpers/isNotNil';

type NonNilParams<TData, TValue> = ICellRendererParams<TData, TValue> & {
  data: NonNullable<ICellRendererParams<TData, TValue>['data']>;
  value: TValue;
  node: ICellRendererParams<TData, TValue>['node'] & {
    data: NonNullable<ICellRendererParams<TData, TValue>['node']['data']>;
  };
};

export const CellRenderer = <
  TData,
  /** The `field` property of the column, if used for rendering */
  TKey extends keyof TData = never,
  TValue = TData[TKey],
>(
  renderer: (nonNilParams: NonNilParams<TData, TValue>) => ReactNode,
  {
    show = true,
    flexProps,
  }: {
    show?: boolean | ((nonNilParams: NonNilParams<TData, TValue>) => boolean);
    flexProps?: FlexProps;
  } = {},
) =>
  function (rendererParams: ICellRendererParams<TData, TValue>) {
    if (!show) return null;
    const { data, node } = rendererParams;
    if (isNotNil(data) && isNotNil(node.data)) {
      // We know we can do this cast safely since we did the isNotNil checks
      const nonNilData = rendererParams as NonNilParams<TData, TValue>;
      if (typeof show === 'function' ? show(nonNilData) : show) {
        return (
          <Flex
            align="center"
            fz="sm"
            gap="xs"
            {...flexProps}
            style={{
              height: '100%',
              // TODO: `.style` can now be a function, need to handle it
              // oxlint-disable-next-line typescript/no-misused-spread
              ...flexProps?.style,
            }}
          >
            {renderer(nonNilData)}
          </Flex>
        );
      }
    }
    return null;
  };
